import { QUESTIONNAIRE_REQUIRED_TIMESTAMP } from '../constants';
import { QUESTIONS, type AnswersContext, type Question } from './questions';
import type { OrgType, QuestionId, QuestionnaireAnswers } from './types';

export interface AdvisorForQuestionnaireGate {
  advisorCreatedDateTime?: Date | string | number | null;
}

export function isQuestionnaireRequiredForAdvisor(
  advisor: AdvisorForQuestionnaireGate | null | undefined,
): boolean {
  if (!advisor?.advisorCreatedDateTime) return false;

  const raw = advisor.advisorCreatedDateTime;
  const ts =
    raw instanceof Date ? raw.getTime() : typeof raw === 'number' ? raw : new Date(raw).getTime();

  if (Number.isNaN(ts)) return false;

  return ts >= QUESTIONNAIRE_REQUIRED_TIMESTAMP;
}

export interface QuestionnaireContext {
  answers: QuestionnaireAnswers;
  orgType: OrgType | null;
  organizationName?: string | null;
  isEdjPilotAdvisor?: boolean;
  isDistributionPartnersRestricted?: boolean;
}

function toCtx(ctx: QuestionnaireContext): AnswersContext {
  return {
    answers: ctx.answers,
    orgType: ctx.orgType,
    organizationName: ctx.organizationName ?? null,
    isEdjPilotAdvisor: ctx.isEdjPilotAdvisor,
    isDistributionPartnersRestricted: ctx.isDistributionPartnersRestricted,
  };
}

function isQuestionVisible(question: Question, ctx: AnswersContext): boolean {
  return question.showIf ? question.showIf(ctx) : true;
}

function hasAnswer(answers: QuestionnaireAnswers, id: QuestionId): boolean {
  const value = answers[id];

  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.length > 0;

  return true;
}

export function getVisibleQuestions(ctx: QuestionnaireContext): Question[] {
  const a = toCtx(ctx);

  return QUESTIONS.filter(q => isQuestionVisible(q, a));
}

export function getNextUnansweredQuestion(ctx: QuestionnaireContext): Question | null {
  return getVisibleQuestions(ctx).find(q => q.required && !hasAnswer(ctx.answers, q.id)) ?? null;
}

export function getQuestionIndex(ctx: QuestionnaireContext, questionId: QuestionId): number {
  return getVisibleQuestions(ctx).findIndex(q => q.id === questionId);
}

export interface QuestionnaireProgress {
  current: number;
  estimatedTotal: number;
}

export function getQuestionnaireProgress(
  ctx: QuestionnaireContext,
  questionId: QuestionId,
): QuestionnaireProgress {
  const visible = getVisibleQuestions(ctx);
  const idx = visible.findIndex(q => q.id === questionId);

  return {
    current: idx >= 0 ? idx + 1 : 1,
    estimatedTotal: Math.max(visible.length, idx + 1),
  };
}

export interface AppliedDefaultsResult {
  answers: QuestionnaireAnswers;
  inferredKeys: QuestionId[];
}

export function applyInferredDefaults(ctx: QuestionnaireContext): AppliedDefaultsResult {
  const a = toCtx(ctx);
  const nextAnswers = { ...ctx.answers } as Record<string, unknown>;
  const inferredKeys: QuestionId[] = [];

  for (const q of QUESTIONS) {
    if (!q.inferDefault) continue;

    if (isQuestionVisible(q, { ...a, answers: nextAnswers as QuestionnaireAnswers })) continue;

    if (hasAnswer(nextAnswers as QuestionnaireAnswers, q.id)) continue;

    const value = q.inferDefault({ ...a, answers: nextAnswers as QuestionnaireAnswers });

    if (value === undefined || value === null) continue;

    nextAnswers[q.id] = value;
    inferredKeys.push(q.id);
  }

  return { answers: nextAnswers as QuestionnaireAnswers, inferredKeys };
}

export function isQuestionnaireComplete(ctx: QuestionnaireContext): boolean {
  return getNextUnansweredQuestion(ctx) == null;
}

export function isQuestionVisibleById(ctx: QuestionnaireContext, id: QuestionId): boolean {
  const q = QUESTIONS.find(x => x.id === id);

  if (!q) return false;

  return isQuestionVisible(q, toCtx(ctx));
}

export function getFirstUnansweredOrLastQuestion(ctx: QuestionnaireContext): Question | null {
  const visible = getVisibleQuestions(ctx);

  if (visible.length === 0) return null;

  const next = visible.find(q => q.required && !hasAnswer(ctx.answers, q.id));

  return next ?? visible[visible.length - 1];
}
